/* eslint-disable jsx-a11y/alt-text */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {loadAttachments, deleteAttachment } from '../../redux/ducks/attachments';
import { setProjectNbAttachments } from '../../redux/ducks/projects';
import { entityId } from '../../redux/ducks/remoteUpdate';
import { RootState, useAppDispatch, useAppSelector } from "../../redux";
import { Attachment } from '../../models';
import { Confirm, Icon } from '..';
import { Badge, Modal } from 'flowbite-react';
import ReactTimeAgo from 'react-time-ago';

interface AttachmentsListProps {
    projectId?: string;
    changeId?: string;
    showChangeSlug?: boolean;
    isBugReport?: boolean;
}

const AttachmentsList = (props: AttachmentsListProps) => {
    const { isBugReport, projectId, changeId, showChangeSlug } = props;
    const { attachments, attachment } = useAppSelector((state: RootState) => state.attachments);
    const dispatch    = useAppDispatch();
    const { t, i18n } = useTranslation();
    const [confirmID, setConfirmID] = useState('');
    const [toDelete, setToDelete]   = useState<Attachment>();
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [allAreVisible, showAll]  = useState({
        project: false,
        changes: false
    });

    useEffect(() => {
        if (isBugReport) {
            Attachment.loadBugReportAttachmentUrl(changeId ?? "");
        } else if (projectId && !isBugReport) {
            const filters: AnyObj = { project: projectId };
            if (changeId)
                filters.change = changeId;
            dispatch(loadAttachments(filters, (err, result) => {
                if (err || changeId || !Array.isArray(result))
                    return;
                dispatch(setProjectNbAttachments(result.length));
            }));
        }
    }, [dispatch, projectId, changeId, isBugReport, attachment]);

    // Ignore Mercure leftovers from other projects / cards still in Redux.
    const scopedAttachments: Attachment[] = useMemo(() => {
        if (!attachments?.length)
            return [];

        return attachments.filter((a: Attachment) => {
            if (projectId && entityId(a.project) !== String(projectId))
                return false;
            if (changeId && entityId(a.change) !== String(changeId))
                return false;
            return true;
        });
    }, [attachments, projectId, changeId]);

    const pictureAttachments: Attachment[] = useMemo(() => {
        if (!scopedAttachments.length)
            return [];

        return scopedAttachments.filter((a: Attachment) => a.isPicture()).sort((a: Attachment, b: Attachment) => {
            if (!a.updatedAt)
                return -1;

            if (!b.updatedAt)
                return 1;

            return a.updatedAt > b.updatedAt ? -1 : 1;
        });
    }, [scopedAttachments]);
    useEffect(() => {
        if (previewIndex === null)
            return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                // Capture + stop so ChangeModal Escape does not close the card.
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                setPreviewIndex(null);
                return;
            }
            if (pictureAttachments.length < 2)
                return;
            if (e.key === 'ArrowRight')
                setPreviewIndex(i => (i === null ? i : (i + 1) % pictureAttachments.length));
            if (e.key === 'ArrowLeft')
                setPreviewIndex(i => (i === null ? i : (i - 1 + pictureAttachments.length) % pictureAttachments.length));
        };

        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [previewIndex, pictureAttachments.length]);

    const handleDeleteFiles = () => {
        dispatch(deleteAttachment(confirmID))
        setToDelete(undefined);
    }

    const askForDelete = (e: AnyObj, attachment: Attachment) => {
        e.preventDefault();
        e.stopPropagation();
        setConfirmID(attachment._id);
        setToDelete(attachment);
    }

    const openPreview = (e: AnyObj, attachment: Attachment) => {
        if (!attachment.isPicture())
            return;

        e.preventDefault();
        const index = pictureAttachments.findIndex((a: Attachment) => a._id === attachment._id);
        if (index >= 0)
            setPreviewIndex(index);
    };

    const stepPreview = (delta: number) => {
        setPreviewIndex(i => (
            i === null ? i : (i + delta + pictureAttachments.length) % pictureAttachments.length
        ));
    };

    const nbProjectAttachments = scopedAttachments.filter((a: Attachment) => !a.change).length;
    const nbChangesAttachments = scopedAttachments.filter((a: Attachment) => !!a.change).length;

    const projectAttachments: Attachment[] = useMemo(() => {
        if (!scopedAttachments.length)
            return [];

        return scopedAttachments.filter((a: Attachment) => !a.change).sort((a: Attachment, b: Attachment) => {
            if (!a.updatedAt)
                return -1;

            if (!b.updatedAt)
                return 1;

            return a.updatedAt > b.updatedAt ? -1 : 1;
        }).slice(0, allAreVisible.project ? scopedAttachments.length : 5);
    }, [scopedAttachments, allAreVisible.project]);

    const changesAttachments: Attachment[] = useMemo(() => {
        if (!scopedAttachments.length)
            return [];

        return scopedAttachments.filter((a: Attachment) => !!a.change).sort((a: Attachment, b: Attachment) => {
            if (!a.updatedAt)
                return -1;

            if (!b.updatedAt)
                return 1;

            return a.updatedAt > b.updatedAt ? -1 : 1;
        }).slice(0, allAreVisible.changes ? scopedAttachments.length : 5);
    }, [scopedAttachments, allAreVisible.changes]);
    const showList = (list: Attachment[]) => list.map((attachment: Attachment) => (
        <a
            key={attachment._id}
            href={attachment.downloadUrl()}
            target="_blank"
            rel="noreferrer"
            title={attachment.isPicture() ? (t('projects.enlarge_image') || '') : undefined}
            className={`flex flex-row my-4 hover:bg-slate-100 dark:hover:bg-gray-700 hover:no-underline rounded border border-transparent hover:border-slate-200${attachment.isPicture() ? ' cursor-zoom-in' : ''}`}
            onClick={(e: AnyObj) => openPreview(e, attachment)}
        >
            <span className="inline-block h-24 w-32 flex justify-center items-center bg-slate-300 hover:no-underline flex rounded">
                {attachment.isPicture() ? (
                    <span
                        className="border inline-block w-full h-full text-2xl text-center font-bold uppercase text-slate-500 bg-cover rounded"
                        style={{
                            backgroundImage: `url(${attachment.downloadUrl()})`,
                            backgroundColor: "#00FF00",
                        }}
                    />
                ) : (
                    <span className="inline-block w-full text-lg text-center font-bold uppercase text-slate-500 rounded">
                        {attachment.getExtension()}
                    </span>
                )}
            </span>
            <span className="h-24 pl-4 flex-auto flex flex-col justify-center">
                <p className="text-sm col-span-2 col-start-2 font-semibold">{attachment.name}</p>
                <p className="text-xs row-start-2 col-start-2 col-span-2 italic text-gray-800 dark:text-gray-100">
                    {t("attachments.upload_file", { firstname: attachment.author?.firstname })}&nbsp;
                    {attachment.createdAt && (
                        <ReactTimeAgo locale={i18n.language} date={attachment.createdAt.toDate()} />
                    )}
                </p>
                {showChangeSlug && attachment.hasChangeDetails() && attachment.change && (
                    <p className="mt-1">
                        <Badge size="xs" color={attachment.change.color()} className="w-fit text-xs">
                            <Icon type={attachment.change.iconName()} size={3} className="inline" />&nbsp;
                            {attachment.change.slugName()}&nbsp;|&nbsp;
                            {attachment.change.localizedTitle(i18n.language)}
                        </Badge>
                    </p>
                )}
                <p className="text-xs text-gray-800 dark:text-gray-100 underline mt-1" onClick={(e: AnyObj) => askForDelete(e, attachment)}>
                    {t('attachments.delete_attachment')}
                </p>
            </span>
        </a>
    ));

    return (
            <>
                <div className="my-3 grid grid-cols-1 lg:grid-cols-2">
                    {projectAttachments?.length > 0 && (
                        <div className={`col-span-${changesAttachments?.length > 0 ? 1 : 2}`}>
                            <h3 className="font-bold">{t('attachments.project_attachments')}</h3>
                            {showList(projectAttachments)}
                            {nbProjectAttachments > 5 && (
                                <p className="mt-5 flex justify-start items-center mb-10">
                                    <span className="text-xs text-white py-1.5 px-2 bg-gray-500 text-center cursor-pointer rounded" onClick={() => showAll({...allAreVisible, project: !allAreVisible.project})}>
                                        {t(allAreVisible.project ? 'attachments.show_less' : 'attachments.show_all', {hidden: nbProjectAttachments - 5})}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}
                    {changesAttachments?.length > 0 && (
                        <div className={`col-span-${projectAttachments?.length > 0 ? 1 : 2}`}>
                            {!changeId && (
                                <h3 className="font-bold">{t('attachments.changes_attachments')}</h3>
                            )}
                            {showList(changesAttachments)}
                            {nbChangesAttachments > 5 && (
                                <p className="mt-5 flex justify-start items-center">
                                    <span className="text-xs text-white py-1.5 px-2 bg-gray-500 text-center cursor-pointer rounded" onClick={() => showAll({...allAreVisible, changes: !allAreVisible.changes})}>
                                        {t(allAreVisible.changes ? 'attachments.show_less' : 'attachments.show_all', {hidden: nbChangesAttachments - 5})}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}
                    <Confirm
                        visible={!!toDelete}
                        title={ t(toDelete?.isPicture() ? 'changes.delete_this_img' : 'changes.delete_this_file') || "" }
                        onConfirm={handleDeleteFiles}
                        onCancel={() => setToDelete(undefined)}
                    />
                </div>
                <Modal show={previewIndex !== null} onClose={() => setPreviewIndex(null)} size="7xl" popup>
                    <Modal.Header />
                    <Modal.Body>
                        <div className="relative flex items-center justify-center min-h-[40vh] px-8">
                            {pictureAttachments.length > 1 && (
                                <button
                                    type="button"
                                    className="absolute left-0 p-2 text-gray-400 hover:text-gray-700 dark:text-gray-200"
                                    onClick={() => stepPreview(-1)}
                                >
                                    <Icon type="left" size={8} />
                                </button>
                            )}
                            {previewIndex !== null && pictureAttachments[previewIndex] && (
                                <img
                                    src={pictureAttachments[previewIndex].downloadUrl()}
                                    alt={pictureAttachments[previewIndex].name}
                                    className="max-h-[85vh] w-auto max-w-full object-contain mx-auto"
                                />
                            )}
                            {pictureAttachments.length > 1 && (
                                <button
                                    type="button"
                                    className="absolute right-0 p-2 text-gray-400 hover:text-gray-700 dark:text-gray-200"
                                    onClick={() => stepPreview(1)}
                                >
                                    <Icon type="right" size={8} />
                                </button>
                            )}
                        </div>
                    </Modal.Body>
                </Modal>
            </>
    );
};

export default AttachmentsList;
